import React, { useEffect, useState } from 'react';
import { ref as storageRef } from 'firebase/storage';
import { safeFetch } from '../../lib/api';
import { storage, uploadBytesWithTimeout } from '../../lib/firebase';
import { RichTextEditor } from '../shared/RichTextEditor';
import { confirmDialog } from '../../lib/confirmStore';
import {
  LEGAL_DOC_TYPES,
  LEGAL_DISCLOSURES,
  LEGAL_DOCUMENT_SLOTS,
  LEGAL_LINK_FIELDS,
  emptyLegalConfig,
  legalCompleteness,
  normaliseLegalConfig,
  type WorkspaceLegalConfig,
} from '../../lib/legalConfig';
import { ATTORNEY_REVIEW_NOTICE, LEGAL_STARTER_TEMPLATES } from '../../lib/legalContentTemplates';

/**
 * Workspace Settings → Legal (owner directive LEGAL_SETTINGS_SPEC.md).
 * Workspace admin only (server enforces settings view/edit permissions);
 * everything saved here is stamped server-side with the caller's own
 * workspace and drives the public legal pages, clickwrap versions,
 * payment screens, the Didit notice, and the CAN-SPAM email footer.
 * Light theme only.
 */
export default function LegalSettingsSection({ workspaceId }: { workspaceId: string }) {
  const [config, setConfig] = useState<WorkspaceLegalConfig>(() => emptyLegalConfig(workspaceId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [requests, setRequests] = useState<any[]>([]);
  const [slotBusy, setSlotBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/legal/config');
      if (data?.config) setConfig(normaliseLegalConfig(data.config, workspaceId));
      const queue = await safeFetch('/api/legal/privacy-requests').catch(() => null);
      if (queue?.requests) setRequests(queue.requests);
    } catch {
      setError('Could not load the legal configuration.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [workspaceId]);

  const save = async () => {
    setSaving(true); setError(''); setNotice('');
    try {
      const data = await safeFetch('/api/legal/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      if (data?.config) setConfig(normaliseLegalConfig(data.config, workspaceId));
      setNotice('Legal configuration saved. Version numbers only change when the text itself changes, so consent records keep pointing at the exact version each user saw.');
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const setLink = (key: string, value: string) =>
    setConfig(c => ({ ...c, links: { ...c.links, [key]: value } }));
  const setBody = (kind: 'documents' | 'disclosures', key: string, html: string) =>
    setConfig(c => ({
      ...c,
      [kind]: {
        ...c[kind],
        [key]: { html, version: (c[kind] as any)[key]?.version || 0 },
      },
    }));

  const uploadSlot = async (slotKey: string, file: File | null) => {
    if (!file) return;
    setSlotBusy(slotKey); setError('');
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Public slots (policies + the BIPA schedule) live under legal/public
      // and are world-readable; internal slots (DPA, bond certificate…)
      // live under legal/internal and stay admin-only in storage.rules.
      const slotDef = LEGAL_DOCUMENT_SLOTS.find(s => s.key === slotKey);
      const path = `workspaces/${workspaceId}/legal/${slotDef?.public ? 'public' : 'internal'}/${slotKey}_${Date.now()}_${safeName}`;
      await uploadBytesWithTimeout(storageRef(storage, path), file, 60000, 3);
      const data = await safeFetch('/api/legal/config/document-slot', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot: slotKey,
          storagePath: path,
          fileName: file.name,
          effectiveDate: new Date().toISOString().slice(0, 10),
          active: true,
        }),
      });
      if (data?.config) setConfig(normaliseLegalConfig(data.config, workspaceId));
      setNotice('Document uploaded and registered as the active version for its slot.');
    } catch (err: any) {
      setError(err?.message || 'Upload failed. Legal documents must be PDFs uploaded by a workspace admin.');
    } finally {
      setSlotBusy('');
    }
  };

  const patchRequest = async (id: string, patch: any) => {
    try {
      await safeFetch(`/api/legal/privacy-requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Could not update the request.');
    }
  };

  const executeDeletion = async (id: string) => {
    if (!(await confirmDialog('Execute deletion for this request now? This permanently deletes the person’s applications, documents and profile in this workspace. Firma, Didit and Stripe steps remain manual and are listed in the completion report.'))) return;
    try {
      const data = await safeFetch(`/api/legal/privacy-requests/${id}/execute-deletion`, { method: 'POST' });
      setNotice(`Deletion executed. Stores processed: ${(data?.report?.stores || []).map((s: any) => `${s.store} (${s.deleted})`).join(', ')}. Manual steps (Firma / Didit / Stripe / backups) are recorded on the request — they are NOT done automatically.`);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Deletion failed.');
    }
  };

  const completeness = legalCompleteness(config);
  const missingCount = completeness.filter(i => !i.configured).length;
  const inputCls = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white';
  const labelCls = 'block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1';
  const whyCls = 'text-[11px] text-slate-400 mt-1 leading-snug';

  if (loading) return <p className="text-sm text-slate-500 py-8">Loading legal configuration…</p>;

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-900" role="note">
        <strong>Attorney review:</strong> {ATTORNEY_REVIEW_NOTICE}
      </div>

      {/* Completeness checklist */}
      <section aria-label="Legal completeness">
        <h3 className="text-sm font-bold text-slate-800 mb-1">Completeness — {completeness.length - missingCount}/{completeness.length} configured</h3>
        <p className="text-xs text-slate-500 mb-3">Anything marked Missing is a live compliance gap for this workspace: the public page shows a neutral “not published yet” state and, for program terms, enrolment is blocked.</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {completeness.map(item => (
            <li key={item.key} className="flex items-start gap-2 border border-slate-100 rounded-xl px-3 py-2 bg-white">
              <span className={`mt-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${item.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {item.configured ? 'CONFIGURED' : 'MISSING'}
              </span>
              <span>
                <span className="block text-xs font-semibold text-slate-700">{item.label}</span>
                <span className="block text-[11px] text-slate-400 leading-snug">{item.why}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Links + contact */}
      <section aria-label="Legal links">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Links &amp; contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {LEGAL_LINK_FIELDS.map(f => (
            <div key={f.key}>
              <label className={labelCls} htmlFor={`legal-link-${f.key}`}>{f.label}{f.optional ? ' (optional)' : ''}</label>
              <input id={`legal-link-${f.key}`} className={inputCls} value={config.links[f.key]} onChange={e => setLink(f.key, e.target.value)} placeholder="https://…" />
              <p className={whyCls}>Why required: {f.why}</p>
            </div>
          ))}
          <div>
            <label className={labelCls} htmlFor="legal-contact-email">Legal / privacy contact email</label>
            <input id="legal-contact-email" className={inputCls} value={config.privacyContactEmail} onChange={e => setConfig(c => ({ ...c, privacyContactEmail: e.target.value }))} placeholder="privacy@…" />
            <p className={whyCls}>Why required: every privacy regime requires a reachable privacy contact, and rights requests need a monitored destination.</p>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="legal-postal">Postal address</label>
            <textarea id="legal-postal" className={inputCls} rows={2} value={config.postalAddress} onChange={e => setConfig(c => ({ ...c, postalAddress: e.target.value }))} placeholder={"Legal entity name\nStreet, City, Region, Postal code, Country"} />
            <p className={whyCls}>Why required: CAN-SPAM requires a valid physical postal address in commercial email; this address is printed in the email footer.</p>
          </div>
        </div>
      </section>

      {/* Policy bodies */}
      <section aria-label="Legal documents text">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Policy &amp; terms text (rendered on the public legal pages)</h3>
        <div className="space-y-5">
          {LEGAL_DOC_TYPES.map(d => (
            <div key={d.key} className="border border-slate-100 rounded-2xl p-4 bg-white">
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-xs font-bold text-slate-700">{d.label} <span className="text-slate-400 font-semibold">({d.path})</span></span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400">v{config.documents[d.key]?.version || 0}</span>
                  <button type="button" className="text-[11px] font-semibold text-brand hover:underline cursor-pointer" onClick={() => setBody('documents', d.key, LEGAL_STARTER_TEMPLATES[d.key] || '')}>
                    Load model template
                  </button>
                </span>
              </div>
              <p className={whyCls}>Why required: {d.why}</p>
              <RichTextEditor value={config.documents[d.key]?.html || ''} onChange={html => setBody('documents', d.key, html)} />
            </div>
          ))}
        </div>
      </section>

      {/* Disclosures */}
      <section aria-label="Disclosure and consent texts">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Disclosures &amp; consent texts</h3>
        <div className="space-y-5">
          {LEGAL_DISCLOSURES.map(d => (
            <div key={d.key} className="border border-slate-100 rounded-2xl p-4 bg-white">
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-xs font-bold text-slate-700">{d.label}</span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400">v{config.disclosures[d.key]?.version || 0}</span>
                  <button type="button" className="text-[11px] font-semibold text-brand hover:underline cursor-pointer" onClick={() => setBody('disclosures', d.key, LEGAL_STARTER_TEMPLATES[d.key] || '')}>
                    Load model template
                  </button>
                </span>
              </div>
              <p className={whyCls}>Why required: {d.why}</p>
              <RichTextEditor value={config.disclosures[d.key]?.html || ''} onChange={html => setBody('disclosures', d.key, html)} />
            </div>
          ))}
        </div>
      </section>

      {/* Document slots */}
      <section aria-label="Legal document uploads">
        <h3 className="text-sm font-bold text-slate-800 mb-1">Legal documents (uploads)</h3>
        <p className="text-xs text-slate-500 mb-3">Each slot holds the active document of record. Uploads go through the platform’s scanned upload pipeline into this workspace’s legal folder.</p>
        <ul className="space-y-2">
          {LEGAL_DOCUMENT_SLOTS.map(slot => {
            const value = config.documentSlots[slot.key];
            return (
              <li key={slot.key} className="border border-slate-100 rounded-2xl px-4 py-3 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-700">{slot.label}</p>
                    <p className={whyCls}>Why required: {slot.why}</p>
                    {value ? (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {value.fileName} · v{value.version} · effective {value.effectiveDate || '—'} · {value.active ? 'Active' : 'Inactive'}
                      </p>
                    ) : (
                      <p className="text-[11px] text-rose-600 mt-1">No document uploaded.</p>
                    )}
                  </div>
                  <label className="px-3 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-50">
                    {slotBusy === slot.key ? 'Uploading…' : value ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={e => { void uploadSlot(slot.key, e.target.files?.[0] || null); e.target.value = ''; }}
                    />
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {notice && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" role="status">{notice}</p>}
      {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
      <button type="button" onClick={save} disabled={saving} className="px-5 py-2.5 btn-primary rounded-xl text-sm font-semibold cursor-pointer disabled:opacity-50">
        {saving ? 'Saving…' : 'Save legal configuration'}
      </button>

      {/* Privacy request queue */}
      <section aria-label="Privacy requests" className="border-t border-slate-100 pt-6">
        <h3 className="text-sm font-bold text-slate-800 mb-1">Privacy requests (data rights queue)</h3>
        <p className="text-xs text-slate-500 mb-3">Requests are due within 30 days of receipt. Verify identity before executing a deletion; Firma, Didit and Stripe deletions are manual steps and are listed on the completion report — they are never marked done automatically.</p>
        {requests.length === 0 ? (
          <p className="text-xs text-slate-400">No privacy requests for this workspace yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-2xl bg-white">
            {requests.map(r => (
              <li key={r.id} className="px-4 py-3 text-xs space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-slate-700 capitalize">{String(r.type).replace('_', ' ')}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-black text-[10px] ${r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                  <span className="text-slate-400">from {r.email || r.userId || 'unknown'} · received {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''} · due {r.slaDueAt ? new Date(r.slaDueAt).toLocaleDateString() : ''}</span>
                  <span className="text-slate-400">{r.verified ? 'identity verified' : 'IDENTITY NOT VERIFIED'}</span>
                </div>
                {r.message && <p className="text-slate-600">{r.message}</p>}
                <div className="flex flex-wrap gap-2">
                  {!r.verified && (
                    <button type="button" className="px-2.5 py-1.5 border border-slate-200 rounded-lg font-semibold cursor-pointer" onClick={() => patchRequest(r.id, { verified: true })}>
                      Mark identity verified
                    </button>
                  )}
                  {r.status !== 'completed' && (
                    <button type="button" className="px-2.5 py-1.5 border border-slate-200 rounded-lg font-semibold cursor-pointer" onClick={() => patchRequest(r.id, { status: 'completed' })}>
                      Mark completed
                    </button>
                  )}
                  {r.type === 'deletion' && r.status !== 'completed' && (
                    <button type="button" className="px-2.5 py-1.5 border border-rose-200 text-rose-700 rounded-lg font-semibold cursor-pointer" onClick={() => executeDeletion(r.id)}>
                      Execute deletion
                    </button>
                  )}
                  {(r.type === 'access' || r.type === 'portability') && r.userId && (
                    <a className="px-2.5 py-1.5 border border-slate-200 rounded-lg font-semibold" href={`/api/legal/privacy-requests/${r.id}/export`} target="_blank" rel="noopener noreferrer">
                      Download export (JSON)
                    </a>
                  )}
                </div>
                {r.deletionReport && (
                  <p className="text-slate-500">
                    Deletion report: {r.deletionReport.stores?.map((s: any) => `${s.store}: ${s.deleted}`).join(' · ')}.
                    Manual steps outstanding: {r.deletionReport.manualSteps?.length || 0} (see request record).
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
